import React, { ReactElement } from "react";
import moment from "moment";
import { ListTableColumn } from "../../../../core/components/list-table";

interface Address {
  country?: string;
  building?: string;
  address?: string;
  addressType?: string;
  contactStatus?: string;
  state?: string;
  district?: string;
  city?: string;
  postalCode?: string;
}

interface Customer {
  civilIdDetails?: {
    address?: Address;
  };
  addresses?: Address[];
}

type ColumnKey =
  | "fullName"
  | "primaryPhone"
  | "primaryEmail"
  | "addresses"
  | "dob"
  | "ageRange"
  | "designation"
  | "dinNumber"
  | "isVerified"
  | "facebookProfile"
  | "linkedinProfile"
  | "panNumber"
  | "gstNumber"
  | "createdAt"
  | "createdBy"
  | "address"
  | "addressType"
  | "contactStatus"
  | "state"
  | "district"
  | "city"
  | "postalCode"
  | "remarks"
  | "updatedBy"
  | "updatedAt"
  | "assistantName"
  | "customerContactType"
  | "assistantNumber"
  | "assistantDesignation"
  | "seniority"
  | "department"
  | "isDirectorOfCompany"
  | "accountManager"
  | "associationStartDate"
  | "associationEndDate";

const personColumns: Record<ColumnKey, ReactElement> = {
  fullName: (
    <ListTableColumn
      label="Customer Name"
      source="fullName"
      column="name"
      sortable
      showDefault
      settingsGroup="Person"
      sortKey="fullName"
    />
  ),
  primaryPhone: (
    <ListTableColumn
      label="Phone Number"
      source="primaryPhone"
      column="phone"
      settingsGroup="Person"
      sortKey="primaryPhone"
      sortable
      showDefault
    />
  ),
  primaryEmail: (
    <ListTableColumn
      label="Email"
      source="primaryEmail"
      column="email"
      settingsGroup="Person"
      sortKey="primaryEmail"
      sortable
      showDefault
    />
  ),
  addresses: (
    <ListTableColumn
      label="Country"
      source="addresses"
      formatContent={(address: Address[]) => address?.[0]?.country}
      column="country"
      settingsGroup="Address"
      excelSource="addresses"
    />
  ),
  dob: (
    <ListTableColumn
      label="Date Of Birth"
      source="dob"
      column="dob"
      formatContent={(d: string | Date | moment.Moment) =>
        moment(d).format("DD MMM YYYY")
      }
      sortable
      sortKey="dob"
      settingsGroup="Person"
      excelSource="dob"
    />
  ),
  ageRange: (
    <ListTableColumn
      label="Age Range"
      source="ageRange"
      column="ageRange"
      settingsGroup="Person"
      excelSource="ageRange"
    />
  ),
  designation: (
    <ListTableColumn
      label="Designation"
      source="designation"
      column="designation"
      formatContent={(value) => value?.name || ""}
      settingsGroup="Person"
      excelSource="designation"
    />
  ),
  dinNumber: (
    <ListTableColumn
      label="DIN Number"
      source="dinNumber"
      column="dinNumber"
      settingsGroup="Person"
      excelSource="dinNumber"
    />
  ),
  isVerified: (
    <ListTableColumn
      label="Contact Type"
      source="isVerified"
      column="isVerified"
      settingsGroup="Person"
      excelSource="isVerified"
    />
  ),
  facebookProfile: (
    <ListTableColumn
      label="Facebook Profile"
      source="facebookProfile"
      column="facebookProfile"
      settingsGroup="Person"
      excelSource="facebookProfile"
    />
  ),
  linkedinProfile: (
    <ListTableColumn
      label="Linkedin Profile"
      source="linkedinProfile"
      column="linkedinProfile"
      settingsGroup="Person"
      excelSource="linkedinProfile"
    />
  ),
  panNumber: (
    <ListTableColumn
      label="PAN Number"
      source="panNumber"
      column="panNumber"
      settingsGroup="Person"
      excelSource="panNumber"
    />
  ),
  gstNumber: (
    <ListTableColumn
      label="GST Number"
      source="gstNumber"
      column="gstNumber"
      settingsGroup="Person"
      excelSource="gstNumber"
    />
  ),
  createdAt: (
    <ListTableColumn
      label="Created Date and Time"
      source="createdAt"
      column="createdAt"
      formatContent={(d: string | Date | moment.Moment) =>
        moment(d).format("DD MMM YYYY hh:mm A")
      }
      sortable
      sortKey="createdAt"
      settingsGroup="Person"
      showDefault
    />
  ),
  createdBy: (
    <ListTableColumn
      label="Created By"
      source="createdBy.name"
      column="createdBy"
      settingsGroup="Person"
      excelSource="createdBy.name"
    />
  ),
  address: (
    <ListTableColumn
      label="Address"
      column="address"
      settingsGroup="Address"
      formatContent={(customer: Customer) =>
        customer.civilIdDetails?.address?.building ||
        customer?.addresses?.[0]?.address
      }
      excelSource="address"
    />
  ),
  addressType: (
    <ListTableColumn
      label="Address Type"
      column="addressType"
      settingsGroup="Address"
      formatContent={(customer: Customer) =>
        customer.civilIdDetails?.address?.addressType ||
        customer?.addresses?.[0]?.addressType
      }
      excelSource="addressType"
    />
  ),
  contactStatus: (
    <ListTableColumn
      label="Contact Status"
      column="contactStatus"
      settingsGroup="Address"
      formatContent={(customer: Customer) =>
        customer.civilIdDetails?.address?.contactStatus ||
        customer?.addresses?.[0]?.contactStatus
      }
      excelSource="contactStatus"
    />
  ),
  state: (
    <ListTableColumn
      label="State"
      column="state"
      settingsGroup="Address"
      formatContent={(customer: Customer) =>
        customer.civilIdDetails?.address?.state ||
        customer?.addresses?.[0]?.state
      }
      excelSource="state"
    />
  ),
  district: (
    <ListTableColumn
      label="District"
      column="district"
      settingsGroup="Address"
      formatContent={(customer: Customer) =>
        customer.civilIdDetails?.address?.district ||
        customer?.addresses?.[0]?.district
      }
      excelSource="district"
    />
  ),
  city: (
    <ListTableColumn
      label="City"
      column="city"
      settingsGroup="Address"
      formatContent={(customer: Customer) =>
        customer.civilIdDetails?.address?.city || customer?.addresses?.[0]?.city
      }
      excelSource="city"
    />
  ),
  postalCode: (
    <ListTableColumn
      label="Postal Code"
      column="postalCode"
      settingsGroup="Address"
      formatContent={(customer: Customer) =>
        customer.civilIdDetails?.address?.postalCode ||
        customer?.addresses?.[0]?.postalCode
      }
      excelSource="postalCode"
    />
  ),
  remarks: (
    <ListTableColumn
      label="Remarks"
      source="remarks"
      column="remarks"
      settingsGroup="Person"
      excelSource="remarks"
    />
  ),
  updatedBy: (
    <ListTableColumn
      label="Last Updated By"
      source="updatedBy.name"
      column="updatedBy"
      settingsGroup="Person"
      excelSource="updatedBy.name"
    />
  ),
  updatedAt: (
    <ListTableColumn
      label="Last Updated Date & Time"
      source="updatedAt"
      column="updatedAt"
      formatContent={(d: string | Date | moment.Moment) =>
        moment(d).format("DD MMM YYYY hh:mm A")
      }
      sortable
      sortKey="updatedAt"
      settingsGroup="Person"
      showDefault
      excelSource="updatedAt"
    />
  ),
  assistantName: (
    <ListTableColumn
      label="Assistant Name"
      source="assistantName"
      column="assistantName"
      settingsGroup="Person"
      excelSource="assistantName"
    />
  ),
  customerContactType: (
    <ListTableColumn
      label="Contact Category"
      source="customerContactType"
      column="customerContactType"
      settingsGroup="Person"
      excelSource="customerContactType"
    />
  ),
  assistantNumber: (
    <ListTableColumn
      label="Assistant Number"
      source="assistantNumber"
      column="assistantNumber"
      settingsGroup="Person"
      excelSource="assistantNumber"
    />
  ),
  assistantDesignation: (
    <ListTableColumn
      label="Assistant Designation"
      source="assistantDesignation"
      column="assistantDesignation"
      settingsGroup="Person"
      excelSource="assistantDesignation"
    />
  ),
  seniority: (
    <ListTableColumn
      label="Seniority"
      source="seniority"
      column="seniority"
      formatContent={(value) => value?.title || ""}
      settingsGroup="Person"
      excelSource="seniority"
    />
  ),
  department: (
    <ListTableColumn
      label="Department"
      source="department"
      column="department"
      formatContent={(value) => value?.title || ""}
      settingsGroup="Person"
      excelSource="department"
    />
  ),
  isDirectorOfCompany: (
    <ListTableColumn
      label="Director With Company"
      source="isDirectorOfCompany"
      column="isDirectorOfCompany"
      settingsGroup="Person"
      excelSource="isDirectorOfCompany"
    />
  ),
  accountManager: (
    <ListTableColumn
      label="Account Manager"
      source="accountManager"
      column="accountManager"
      settingsGroup="Person"
      excelSource="accountManager"
    />
  ),
  associationStartDate: (
    <ListTableColumn
      label="Association Start Date"
      source="associationStartDate"
      column="associationStartDate"
      formatContent={(d: string | Date | moment.Moment) =>
        moment(d).format("DD MMM YYYY hh:mm A")
      }
      sortable
      settingsGroup="Person"
      excelSource="associationStartDate"
    />
  ),
  associationEndDate: (
    <ListTableColumn
      label="Association End Date"
      source="associationEndDate"
      column="associationEndDate"
      formatContent={(d: string | Date | moment.Moment) =>
        moment(d).format("DD MMM YYYY hh:mm A")
      }
      sortable
      settingsGroup="Person"
      excelSource="associationEndDate"
    />
  ),
};

const createColumnGroup = (columnKeys: ColumnKey[]) =>
  columnKeys.map((key) => personColumns[key]);

export const PersonColumns = createColumnGroup([
  "fullName",
  "primaryPhone",
  "primaryEmail",
  "addresses",
  "dob",
  "ageRange",
  "designation",
  "dinNumber",
  "isVerified",
  "facebookProfile",
  "linkedinProfile",
  "panNumber",
  "gstNumber",
  "createdAt",
  "createdBy",
  "address",
  "addressType",
  "contactStatus",
  "state",
  "district",
  "city",
  "postalCode",
  "remarks",
  "updatedBy",
  "updatedAt",
  "assistantName",
  "customerContactType",
  "assistantNumber",
  "assistantDesignation",
  "seniority",
  "department",
  "isDirectorOfCompany",
  "accountManager",
  "associationStartDate",
  "associationEndDate"
]);

export const SelectedColumns = createColumnGroup([
  "fullName",
  "primaryPhone",
  "primaryEmail",
  "addresses",
  "dob",
  "ageRange",
  "designation",
  "dinNumber",
  "isVerified",
  "facebookProfile",
  "linkedinProfile",
  "panNumber",
  "gstNumber",
  "createdAt",
  "createdBy",
  "address",
  "addressType",
  "contactStatus",
  "state",
  "district",
  "city",
  "postalCode",
  "remarks",
  "updatedBy",
  "updatedAt",
  "assistantName",
  "customerContactType",
  "assistantNumber",
  "assistantDesignation",
  "seniority",
  "department",
  "isDirectorOfCompany",
  "accountManager",
  "associationStartDate",
  "associationEndDate",
]);